import React from 'react';
import { createPortal } from 'react-dom';

interface ConfirmDialogProps {
    open: boolean;
    title: string;
    message: React.ReactNode;
    confirmLabel?: string;
    cancelLabel?: string;
    /** 'primary' = teal confirm button, 'danger' = red confirm button. */
    tone?: 'primary' | 'danger';
    /** Disables the buttons and backdrop-dismiss while an action is in flight. */
    busy?: boolean;
    onConfirm: () => void;
    onCancel: () => void;
}

/**
 * Lightweight confirmation modal, styled to match the app's other dialogs.
 * Backdrop click and the × / Cancel buttons all cancel (unless `busy`).
 */
export const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
    open, title, message, confirmLabel = 'Confirm', cancelLabel = 'Cancel',
    tone = 'primary', busy = false, onConfirm, onCancel,
}) => {
    if (!open) return null;

    const confirmClass = tone === 'danger'
        ? 'bg-mapper-red text-white'
        : 'bg-mapper-teal text-white shadow-mapper-btn';

    return createPortal(
        <div
            onClick={busy ? undefined : onCancel}
            className="mapper-ui font-sans fixed inset-0 flex items-center justify-center z-[120] p-5"
            style={{ background: 'rgba(24,33,48,.42)' }}
        >
            <div
                onClick={e => e.stopPropagation()}
                className="bg-mapper-white rounded-2xl max-w-[460px] w-full overflow-hidden shadow-mapper-modal text-mapper-text"
            >
                <div className="px-6 py-5 border-b border-mapper-border-3 flex items-center justify-between gap-4">
                    <h3 className="text-base font-bold m-0">{title}</h3>
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        aria-label="Close"
                        className="w-[30px] h-[30px] rounded-lg text-mapper-muted-3 text-xl flex items-center justify-center shrink-0 disabled:opacity-50"
                    >
                        ×
                    </button>
                </div>

                <div className="px-6 py-[22px]">
                    <p className="text-sm text-mapper-body-text-3 leading-[1.6] m-0">{message}</p>
                </div>

                <div className="px-6 py-4 border-t border-mapper-border-3 flex items-center justify-end gap-3">
                    <button
                        onClick={onCancel}
                        disabled={busy}
                        className="px-4 py-2 rounded-[9px] text-[13px] font-semibold text-mapper-muted disabled:opacity-50"
                    >
                        {cancelLabel}
                    </button>
                    <button
                        onClick={onConfirm}
                        disabled={busy}
                        className={`px-4 py-2 rounded-[9px] text-[13px] font-semibold disabled:opacity-70 ${confirmClass}`}
                    >
                        {busy ? 'Working…' : confirmLabel}
                    </button>
                </div>
            </div>
        </div>,
        document.body
    );
};
